import { formatMoney, savingsPercent } from "../shared/checkout-catalog.ts";

const API_BASE = "/api/checkout";

async function requestJson(path, options = {}) {
  const response = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers: {
      Accept: "application/json",
      ...(options.body ? { "Content-Type": "application/json" } : {}),
      ...options.headers,
    },
  });
  const payload = await response.json().catch(() => ({ message: "The checkout service returned an unreadable response." }));
  if (!response.ok) {
    const error = new Error(payload.message || "Checkout is temporarily unavailable.");
    error.field = payload.field;
    throw error;
  }
  return payload;
}

function uuid() {
  return crypto.randomUUID();
}

export function initVipCheckout() {
  const root = document.querySelector("[data-checkout]");
  if (!root) return;

  const form = root.querySelector("[data-checkout-form]");
  const panels = [...root.querySelectorAll("[data-checkout-step]")];
  const stepItems = [...root.querySelectorAll("[data-step-item]")];
  const packageGrid = root.querySelector("[data-package-grid]");
  const locationSelect = root.querySelector("[data-location-select]");
  const accountGroup = root.querySelector("[data-account-group]");
  const accountInput = root.querySelector("[name='accountNumber']");
  const nextButton = root.querySelector("[data-next-step]");
  const backButton = root.querySelector("[data-prev-step]");
  const submitButton = root.querySelector("[data-submit-checkout]");
  const controls = root.querySelector("[data-checkout-controls]");
  const modeBadge = root.querySelector("[data-checkout-mode]");
  const status = root.querySelector("[data-checkout-status]");
  const boundary = root.querySelector("[data-checkout-boundary]");
  const success = root.querySelector("[data-checkout-success]");
  const summary = root.querySelector("[data-order-summary]");
  const orderAgreement = root.querySelector("[data-order-agreement]");
  const reviewIntro = root.querySelector("[data-review-intro]");
  const progress = root.querySelector("[data-loader-progress]");
  const state = {
    step: 1,
    config: null,
    packageId: "",
    submitting: false,
    idempotencyKey: uuid(),
  };

  function announce(message, tone = "neutral") {
    status.textContent = message;
    status.dataset.tone = tone;
  }

  function clearErrors() {
    root.querySelectorAll("[data-field-error]").forEach((item) => { item.textContent = ""; });
    root.querySelectorAll("[aria-invalid='true']").forEach((item) => item.removeAttribute("aria-invalid"));
  }

  function fieldError(field, message) {
    const target = root.querySelector(`[data-field-error='${field}']`);
    const input = field === "packageId"
      ? root.querySelector("[name='packageId']")
      : root.querySelector(`[name='${field}']`);
    if (target) target.textContent = message;
    if (input) {
      input.setAttribute("aria-invalid", "true");
      input.focus();
    }
    announce(message, "error");
  }

  function selectedFlow() {
    return new FormData(form).get("flow") || "activation";
  }

  function selectedPackage() {
    return state.config?.packages.find((item) => item.id === state.packageId) ?? null;
  }

  function selectedLocation() {
    return state.config?.locations.find((item) => item.id === locationSelect.value) ?? null;
  }

  function renderSummary() {
    const item = selectedPackage();
    const location = selectedLocation();
    const flow = selectedFlow();
    if (!item || !location) return;
    summary.innerHTML = `
      <div><span>Action</span><strong>${flow === "activation" ? "New card" : "Reload card"}</strong></div>
      <div><span>Card value</span><strong>${formatMoney(item.valueCents)}</strong></div>
      <div><span>You pay</span><strong>${formatMoney(item.priceCents)}</strong></div>
      <div><span>Location</span><strong>${location.city}</strong></div>
      ${flow === "reload" ? `<div><span>Account</span><strong>•••• ${accountInput.value.slice(-4)}</strong></div>` : ""}
    `;
  }

  function showStep(step, { focus = true } = {}) {
    state.step = step;
    root.style.setProperty("--checkout-progress", String((step - 1) / 3));
    progress.textContent = `Bay ${String(step).padStart(2, "0")} / 04`;
    panels.forEach((panel) => { panel.hidden = Number(panel.dataset.checkoutStep) !== step; });
    stepItems.forEach((item, index) => {
      if (index + 1 === step) item.setAttribute("aria-current", "step");
      else item.removeAttribute("aria-current");
      item.dataset.complete = String(index + 1 < step);
    });
    backButton.hidden = step === 1;
    nextButton.hidden = step === 4;
    submitButton.hidden = step !== 4;
    if (step === 4) renderSummary();
    clearErrors();
    announce(step === 4 ? "Review your order details before continuing." : "Your progress stays on this page.");
    if (focus) panels.find((panel) => Number(panel.dataset.checkoutStep) === step)?.querySelector("legend")?.focus();
  }

  function validateStep() {
    clearErrors();
    if (state.step === 2 && !state.packageId) {
      fieldError("packageId", "Choose a card value to continue.");
      return false;
    }
    if (state.step === 3) {
      if (!locationSelect.value) {
        fieldError("locationId", "Choose your usual Iowa Clean Car location.");
        return false;
      }
      if (selectedFlow() === "reload" && !/^\d{8}$/.test(accountInput.value.trim())) {
        fieldError("accountNumber", "Enter the wash-card number in the published 12345678 format.");
        return false;
      }
    }
    return true;
  }

  function renderPackages(packages) {
    packageGrid.innerHTML = packages.map((item) => `
      <label class="package-option">
        <input type="radio" name="packageId" value="${item.id}" aria-describedby="package-error" />
        <span class="package-option__load"><small>Load</small>${formatMoney(item.valueCents)}</span>
        <span class="package-option__price">Pay ${formatMoney(item.priceCents)}</span>
        <span class="package-option__save">Save ${savingsPercent(item)}%</span>
      </label>
    `).join("");
    packageGrid.removeAttribute("aria-busy");
    packageGrid.addEventListener("change", (event) => {
      if (event.target.matches("[name='packageId']")) {
        state.packageId = event.target.value;
        clearErrors();
      }
    });
  }

  function renderLocations(locations) {
    locationSelect.insertAdjacentHTML("beforeend", locations.map((location) =>
      `<option value="${location.id}">${location.name} — ${location.city}</option>`
    ).join(""));
  }

  function setFlow(flow) {
    const isReload = flow === "reload";
    accountGroup.hidden = !isReload;
    accountInput.required = isReload;
    orderAgreement.textContent = isReload
      ? "I confirm the card value, location, and wash-card number above are correct."
      : "I confirm the card value and location above are correct.";
    if (!isReload) accountInput.value = "";
  }

  async function loadConfig() {
    try {
      const config = await requestJson("/config");
      state.config = config;
      renderPackages(config.packages);
      renderLocations(config.locations);
      const isMock = config.mode === "mock";
      modeBadge.textContent = isMock ? "Order review" : "PayPal connected";
      modeBadge.dataset.mode = isMock ? "ready" : config.mode;
      submitButton.textContent = isMock ? "Review order" : "Continue to PayPal";
      reviewIntro.textContent = isMock
        ? "Reviewing this order does not charge you. Call the office after review to place it."
        : "Nothing is charged until PayPal reports a completed capture.";
      announce(isMock ? "Choose a card type to build an order. Nothing is charged on this site." : "Secure checkout is connected.", isMock ? "warning" : "success");
      await handleReturnFromPayPal(config);
    } catch (error) {
      modeBadge.textContent = "Checkout unavailable";
      modeBadge.dataset.mode = "error";
      packageGrid.innerHTML = "<p class='checkout-empty'>Package service unavailable. The published values above remain available for reference.</p>";
      announce(error.message, "error");
      nextButton.disabled = true;
    }
  }

  async function submitCheckout(event) {
    event.preventDefault();
    if (state.submitting || !validateStep()) return;
    const agreement = form.querySelector("[name='orderAgreement']");
    if (!agreement.checked) {
      fieldError("orderAgreement", "Confirm the order details before continuing.");
      return;
    }
    const item = selectedPackage();
    const location = selectedLocation();
    if (!item || !location) return;

    state.submitting = true;
    submitButton.disabled = true;
    submitButton.dataset.loading = "true";
    submitButton.textContent = "Preparing order…";
    const isMock = state.config?.mode === "mock";
    announce(isMock ? "Checking the order details…" : "Validating the package and preparing a secure order…", "loading");
    try {
      const result = await requestJson("/create", {
        method: "POST",
        body: JSON.stringify({
          flow: selectedFlow(),
          packageId: item.id,
          locationId: location.id,
          accountNumber: selectedFlow() === "reload" ? accountInput.value.trim() : null,
          idempotencyKey: state.idempotencyKey,
        }),
      });
      if (result.mode === "paypal" && result.approvalUrl) {
        announce("Order accepted. Opening PayPal…", "success");
        window.location.assign(result.approvalUrl);
        return;
      }
      controls.hidden = true;
      panels.forEach((panel) => { panel.hidden = true; });
      resetBoundaryContent();
      boundary.hidden = false;
      boundary.querySelector("[data-boundary-reference]").textContent = result.reference;
      boundary.querySelector("[data-boundary-amount]").textContent = formatMoney(result.summary.priceCents);
      boundary.querySelector("[data-boundary-value]").textContent = formatMoney(result.summary.valueCents);
      boundary.querySelector("[data-boundary-location]").textContent = result.summary.locationName;
      if (result.mode === "mock") boundary.querySelector("[data-boundary-reference]").textContent = "Not submitted";
      announce("Order review complete. No payment has been taken.", "warning");
      accountInput.value = "";
      window.requestAnimationFrame(() => boundary.scrollIntoView({ block: "start", behavior: "smooth" }));
    } catch (error) {
      if (error.field) fieldError(error.field, error.message);
      announce(error.message, "error");
    } finally {
      state.submitting = false;
      submitButton.disabled = false;
      submitButton.dataset.loading = "false";
      submitButton.textContent = state.config?.mode === "mock" ? "Review order" : "Continue to PayPal";
    }
  }

  function resetBoundaryContent() {
    boundary.querySelector("[data-boundary-label]").textContent = "Order handoff";
    boundary.querySelector("[data-boundary-title]").textContent = "Your order is ready to place.";
    boundary.querySelector("[data-boundary-copy]").textContent = "Review is complete. Call the office to purchase or reload the VIP card; payment is not collected on this proposal site.";
    boundary.querySelector("[data-boundary-details]").hidden = false;
  }

  function resetCheckout() {
    form.reset();
    state.packageId = "";
    state.idempotencyKey = uuid();
    resetBoundaryContent();
    boundary.hidden = true;
    success.hidden = true;
    controls.hidden = false;
    setFlow("activation");
    showStep(1);
  }

  async function handleReturnFromPayPal(config) {
    const params = new URLSearchParams(window.location.search);
    const checkoutState = params.get("checkout");
    if (!checkoutState) return;
    boundary.querySelector("[data-boundary-label]").textContent = "Payment handoff";
    controls.hidden = true;
    panels.forEach((panel) => { panel.hidden = true; });
    root.scrollIntoView({ block: "start", behavior: "instant" });
    if (checkoutState === "cancelled") {
      boundary.hidden = false;
      boundary.querySelector("[data-boundary-title]").textContent = "Checkout cancelled.";
      boundary.querySelector("[data-boundary-copy]").textContent = "PayPal did not report a completed payment. You can return to the review step without losing the published package values.";
      boundary.querySelector("[data-boundary-details]").hidden = true;
      announce("Checkout was cancelled. No card load has been promised.", "error");
      return;
    }
    const orderId = params.get("token");
    if (checkoutState !== "approved" || !orderId || config.mode !== "paypal") return;
    boundary.hidden = false;
    boundary.querySelector("[data-boundary-title]").textContent = "Confirming payment…";
    boundary.querySelector("[data-boundary-copy]").textContent = "PayPal approved the order. Iowa Clean Car is checking the server-side capture result before showing confirmation.";
    boundary.querySelector("[data-boundary-details]").hidden = true;
    try {
      const result = await requestJson("/capture", { method: "POST", body: JSON.stringify({ orderId }) });
      if (result.status !== "COMPLETED") throw new Error("PayPal has not reported a completed payment.");
      boundary.hidden = true;
      success.hidden = false;
      success.querySelector("[data-success-title]").textContent = "Payment confirmed.";
      success.querySelector("[data-success-copy]").textContent = result.message;
      announce("PayPal reported a completed capture.", "success");
    } catch (error) {
      announce(error.message, "error");
      boundary.querySelector("[data-boundary-title]").textContent = "Confirmation needs attention.";
      boundary.querySelector("[data-boundary-copy]").textContent = error.message;
    }
  }

  form.addEventListener("change", (event) => {
    if (event.target.matches("[name='flow']")) setFlow(event.target.value);
    if (event.target.matches("[name='orderAgreement']")) clearErrors();
  });
  nextButton.addEventListener("click", () => { if (validateStep()) showStep(Math.min(4, state.step + 1)); });
  backButton.addEventListener("click", () => showStep(Math.max(1, state.step - 1)));
  form.addEventListener("submit", submitCheckout);
  root.querySelectorAll("[data-reset-checkout]").forEach((button) => button.addEventListener("click", resetCheckout));
  root.querySelector("[data-edit-checkout]").addEventListener("click", () => {
    boundary.hidden = true;
    controls.hidden = false;
    showStep(4);
  });

  setFlow("activation");
  showStep(1, { focus: false });
  loadConfig();
}
